import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import { DataTable, DataTableSortEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Dialog } from 'primereact/dialog';
import { Image } from 'primereact/image';
import { PaginatorPageChangeEvent } from 'primereact/paginator';
import { toast } from 'react-toastify';
import { CSMasterDDLEntity, DDLEntity, PaginateRespEntity, SystemDDLDetailsEntity } from '../../../interface';
import { APIResponseEntity } from '../../../interface/api-responce';
import {
    BOMCSCodeDDLEntity,
    BOMFormulaFunctionEntity,
    BOMFormulaVariableEntity,
    BOMModelCascadeEntity,
    BOMModelWiseMappingDetailsEntity,
    BOMModelWiseMappingListReqEntity,
    BOMOrientationDDLEntity
} from '../../../interface/settings/bom-mapping';
import { CSMasterTypeDDLEntity } from '../../../interface/settings/cs-master';
import {
    APICSMasterDDL,
    APIFrameColorsDDL,
    APIGetSystemDDL,
    APIToCopyBOMModelWiseMappings,
    APIToCreateBOMModelWiseMapping,
    APIToDeleteBOMModelWiseMapping,
    APIToExportBOMModelWiseMappings,
    APIToGetBOMCopyItems,
    APIToGetBOMCSCodesDDL,
    APIToGetBOMReferenceModulesDDL,
    APIToGetBOMFormulaVariables,
    APIToGetBOMModelWiseMappings,
    APIToGetBOMOrientationDDL,
    APIToGetBomMappingUOMDDL,
    APIToGetMappedCSMasterTypes,
    APIToUpdateBOMModelWiseMapping
} from '../../../service/api-service';
import { checkActionAuthorization, downloadCsvFile } from '../../../service/shared.service';
import { emptyMultiSelectSearchMessage, toastOptObj } from '../../../utils/constants';
import { BOMLineItemSourceEnum } from '../../../utils/enum';
import { IsNullOrEmptyArray, IsStringNullEmptyOrUndefined } from '../../../utils/null-check';
import { sortSelectedFirst } from '../../../utils/shares';
import Loader from '../../../components/loader';
import DeleteConfirmationDialog from '../../../components/delete-confirmation';
import PrimePaginator from '../../../components/prime-paginator';
import './bom-mapping.css';
import { BOMFormulaEditor, formulaVerdictOf } from './bom-formula-editor';

const EMPTY_CASCADE: BOMModelCascadeEntity = { systemId: '', subSystemId: '', systemTypeId: '', orientation: '' };

const DEFAULT_BOM_FILTER_REQ: BOMModelWiseMappingListReqEntity = {
    pageSize: 10,
    pageNumber: 0,
    searchText: '',
    sortColumn: 'createdAt',
    sortOrder: '-1',
    csProductCodes: [],
    csMasterTypeIds: [],
    frameColorIds: [],
    references: [],
    moduleIds: []
};

const MODULE_TYPE_LABELS: { [key: string]: string } = {
    handles: 'HANDLE',
    locks: 'LOCK',
    glassFinishes: 'FINISH',
    glassVariants: 'VARIANT'
};

const BOM_REFERENCE_OPTIONS: DDLEntity[] = [
    { id: 'MANUAL', name: 'Manual' },
    { id: 'handles', name: 'Handle' },
    { id: 'locks', name: 'Lock' },
    { id: 'glassFinishes', name: 'Finish' },
    { id: 'glassVariants', name: 'Variant' }
];

const byName = (a: DDLEntity, b: DDLEntity): number => a.name.localeCompare(b.name);

const subSystemOptionsOf = (systems: SystemDDLDetailsEntity[], systemId: string): DDLEntity[] => {
    const system = systems.find((item) => item.systemId === systemId);
    return system ? system.mappings.map((mapping) => ({ id: mapping.subSystemId, name: mapping.subSystemName })).sort(byName) : [];
};

const systemTypeOptionsOf = (systems: SystemDDLDetailsEntity[], systemId: string, subSystemId: string): DDLEntity[] => {
    const system = systems.find((item) => item.systemId === systemId);
    const mapping = system ? system.mappings.find((item) => item.subSystemId === subSystemId) : undefined;
    return mapping ? mapping.systemTypesMapping.map((type) => ({ id: type.systemTypeId, name: type.systemTypeName })).sort(byName) : [];
};

/** one orientation stands for every active model of the combination that has it; the label says how many */
const orientationOptionLabel = (option: BOMOrientationDDLEntity): string => {
    return option.modelCount > 1 ? option.name + ' (' + option.modelCount + ' models)' : option.name;
};

const isCascadeComplete = (cascade: BOMModelCascadeEntity): boolean => {
    return !IsStringNullEmptyOrUndefined(cascade.systemId)
        && !IsStringNullEmptyOrUndefined(cascade.subSystemId)
        && !IsStringNullEmptyOrUndefined(cascade.systemTypeId);
};

const isOkResponse = (resp: AxiosResponse<APIResponseEntity>): boolean => {
    return !!resp && !!resp.data && (resp.status === 200 || resp.status === 201) && !!resp.data.status;
};

type CascadeFieldsProps = {
    value: BOMModelCascadeEntity;
    systems: SystemDDLDetailsEntity[];
    orientations: BOMOrientationDDLEntity[];
    orientationsLoading: boolean;
    onChange: (next: BOMModelCascadeEntity) => void;
    fieldClassName: string;
    /** several orientations at once (copy target); the cascade's own orientation is then unused */
    multiOrientation?: { value: string[]; onChange: (ids: string[]) => void };
};

/** the four dependent dropdowns; changing a parent clears everything below it */
function CascadeFields({ value, systems, orientations, orientationsLoading, onChange, fieldClassName, multiOrientation }: CascadeFieldsProps) {
    const systemOptions: DDLEntity[] = useMemo(
        () => systems.map((item) => ({ id: item.systemId, name: item.systemName })).sort(byName),
        [systems]
    );
    const subSystemOptions: DDLEntity[] = useMemo(() => subSystemOptionsOf(systems, value.systemId), [systems, value.systemId]);
    const systemTypeOptions: DDLEntity[] = useMemo(() => systemTypeOptionsOf(systems, value.systemId, value.subSystemId), [systems, value.systemId, value.subSystemId]);
    const orientationOptions: DDLEntity[] = useMemo(
        () => orientations.map((option) => ({ id: option.id, name: orientationOptionLabel(option) })).sort(byName),
        [orientations]
    );
    // the options arrive after the system type is picked, so an empty list only means "none" once the request is back
    const pickOrientationLabel: string = multiOrientation ? 'Select orientations' : 'Select orientation';
    const orientationPlaceholder: string = !isCascadeComplete(value)
        ? pickOrientationLabel
        : orientationsLoading
            ? 'Loading orientations...'
            : IsNullOrEmptyArray(orientationOptions) ? 'No active model for this combination' : pickOrientationLabel;

    return (
        <>
            <div className={fieldClassName}>
                <label>SYSTEM</label>
                <Dropdown
                    value={value.systemId || null}
                    options={systemOptions}
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select system"
                    filter
                    resetFilterOnHide
                    emptyFilterMessage={emptyMultiSelectSearchMessage}
                    onChange={(e) => onChange({ systemId: e.value || '', subSystemId: '', systemTypeId: '', orientation: '' })}
                />
            </div>
            <div className={fieldClassName}>
                <label>SUB SYSTEM</label>
                <Dropdown
                    value={value.subSystemId || null}
                    options={subSystemOptions}
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select sub system"
                    filter
                    resetFilterOnHide
                    disabled={IsStringNullEmptyOrUndefined(value.systemId)}
                    emptyFilterMessage={emptyMultiSelectSearchMessage}
                    onChange={(e) => onChange({ ...value, subSystemId: e.value || '', systemTypeId: '', orientation: '' })}
                />
            </div>
            <div className={fieldClassName}>
                <label>SYSTEM TYPE</label>
                <Dropdown
                    value={value.systemTypeId || null}
                    options={systemTypeOptions}
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select system type"
                    filter
                    resetFilterOnHide
                    disabled={IsStringNullEmptyOrUndefined(value.subSystemId)}
                    emptyFilterMessage={emptyMultiSelectSearchMessage}
                    onChange={(e) => onChange({ ...value, systemTypeId: e.value || '', orientation: '' })}
                />
            </div>
            <div className={fieldClassName}>
                <label>ORIENTATION</label>
                {multiOrientation
                    ? <MultiSelect
                        value={multiOrientation.value}
                        options={orientationOptions}
                        optionLabel="name"
                        optionValue="id"
                        placeholder={orientationPlaceholder}
                        maxSelectedLabels={2}
                        selectedItemsLabel="{0} selected"
                        filter
                        resetFilterOnHide
                        disabled={!isCascadeComplete(value)}
                        emptyFilterMessage={emptyMultiSelectSearchMessage}
                        onChange={(e) => multiOrientation.onChange(e.value || [])}
                    />
                    : <Dropdown
                        value={value.orientation || null}
                        options={orientationOptions}
                        optionLabel="name"
                        optionValue="id"
                        placeholder={orientationPlaceholder}
                        filter
                        resetFilterOnHide
                        disabled={!isCascadeComplete(value)}
                        emptyFilterMessage={emptyMultiSelectSearchMessage}
                        onChange={(e) => onChange({ ...value, orientation: e.value || '' })}
                    />}
            </div>
        </>
    );
}

function BOMMapping() {

    const canList: boolean = checkActionAuthorization('list', 'BOM Mapping');
    const canCreate: boolean = checkActionAuthorization('create', 'BOM Mapping');
    const canUpdate: boolean = checkActionAuthorization('update', 'BOM Mapping');
    const canDelete: boolean = checkActionAuthorization('delete', 'BOM Mapping');
    const canCopy: boolean = checkActionAuthorization('upgrade', 'Allow to Copy BOM');
    const canExport: boolean = checkActionAuthorization('upgrade', 'Allow to Export BOM Mapping');

    const [loadingCount, setLoadingCount] = useState<number>(0);

    const [systems, setSystems] = useState<SystemDDLDetailsEntity[]>([]);
    const [csMasterDDL, setCSMasterDDL] = useState<CSMasterDDLEntity[]>([]);
    const [csCodeOptions, setCSCodeOptions] = useState<BOMCSCodeDDLEntity[]>([]);

    const [filterCascade, setFilterCascade] = useState<BOMModelCascadeEntity>(EMPTY_CASCADE);
    const [filterOrientations, setFilterOrientations] = useState<BOMOrientationDDLEntity[]>([]);
    const [orientationsLoading, setOrientationsLoading] = useState<boolean>(false);
    const [filterReq, setFilterReq] = useState<BOMModelWiseMappingListReqEntity>(DEFAULT_BOM_FILTER_REQ);
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [csMasterTypeOptions, setCSMasterTypeOptions] = useState<CSMasterTypeDDLEntity[]>([]);
    const [frameColorOptions, setFrameColorOptions] = useState<DDLEntity[]>([]);
    // picks made inside an open panel; they reach filterReq when the panel closes
    const [pendingCSProductCodes, setPendingCSProductCodes] = useState<string[]>([]);
    const [pendingCSMasterTypeIds, setPendingCSMasterTypeIds] = useState<string[]>([]);
    const [pendingFrameColorIds, setPendingFrameColorIds] = useState<string[]>([]);
    const [pendingReferences, setPendingReferences] = useState<string[]>([]);
    const [moduleDDLMap, setModuleDDLMap] = useState<{ [moduleType: string]: DDLEntity[] }>({});
    const [pendingModuleSelections, setPendingModuleSelections] = useState<{ [moduleType: string]: string[] }>({});

    const [records, setRecords] = useState<BOMModelWiseMappingDetailsEntity[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [hasFetched, setHasFetched] = useState<boolean>(false);

    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [editRecordId, setEditRecordId] = useState<string>('');
    // a line linked to the model's CS master mappings keeps its CS master; only its formula is edited
    const [editLinked, setEditLinked] = useState<boolean>(false);
    // a line that already holds a formula keeps its CS master too
    const [editHasFormula, setEditHasFormula] = useState<boolean>(false);
    const [dialogContext, setDialogContext] = useState<string>('');
    // add: every picked CS master; edit: the single CS master of that line
    const [csProductCodes, setCSProductCodes] = useState<string[]>([]);
    // CS masters pinned on top of the open picker, null while it is closed so the order follows the picks
    const [pinnedCSProductCodes, setPinnedCSProductCodes] = useState<string[] | null>(null);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [selectedUomId, setSelectedUomId] = useState<string>('');
    const [uomOptions, setUomOptions] = useState<DDLEntity[]>([]);

    const selectedUomName = useMemo(() => {
        return uomOptions.find((item) => item.id === selectedUomId)?.name || '';
    }, [uomOptions, selectedUomId]);

    // Formula & simulator state for BOM line items
    const [ruleItemName, setRuleItemName] = useState<string>('');
    const [ruleItemCode, setRuleItemCode] = useState<string>('');
    const [QtyFormula, setQtyFormula] = useState<string>('1*D');
    // the vocabulary of a formula as the backend defines it, each variable with how its value is found
    const [formulaVariables, setFormulaVariables] = useState<BOMFormulaVariableEntity[]>([]);
    const [formulaFunctions, setFormulaFunctions] = useState<BOMFormulaFunctionEntity[]>([]);

    const variableKeys: string[] = useMemo(() => formulaVariables.map((v) => v.key), [formulaVariables]);
    // the preview's verdict: the formula must evaluate, and to a number
    const formulaVerdict = useMemo(() => formulaVerdictOf(QtyFormula, variableKeys), [QtyFormula, variableKeys]);
    const previewError: string = formulaVerdict.previewError;

    // copy: the filter bar orientation is the source, the dialog cascade with its picked orientations is the target
    const [isCopyDialogOpen, setIsCopyDialogOpen] = useState<boolean>(false);
    const [copyCascade, setCopyCascade] = useState<BOMModelCascadeEntity>(EMPTY_CASCADE);
    const [copyTargetOrientations, setCopyTargetOrientations] = useState<string[]>([]);
    const [copyOrientations, setCopyOrientations] = useState<BOMOrientationDDLEntity[]>([]);
    const [copyOrientationsLoading, setCopyOrientationsLoading] = useState<boolean>(false);
    const [isCopySubmitted, setIsCopySubmitted] = useState<boolean>(false);
    const [copyItems, setCopyItems] = useState<CSMasterDDLEntity[]>([]);
    const [copyCSProductCodes, setCopyCSProductCodes] = useState<string[]>([]);
    const [copyItemsLoading, setCopyItemsLoading] = useState<boolean>(false);

    const isEdit: boolean = !IsStringNullEmptyOrUndefined(editRecordId);
    const isFiltered: boolean = !IsStringNullEmptyOrUndefined(filterReq.searchText || '') || !IsStringNullEmptyOrUndefined(filterCascade.systemId)
        || !IsNullOrEmptyArray(filterReq.csProductCodes || [])
        || !IsNullOrEmptyArray(filterReq.csMasterTypeIds || []) || !IsNullOrEmptyArray(filterReq.frameColorIds || [])
        || !IsNullOrEmptyArray(filterReq.references || []) || !IsNullOrEmptyArray(filterReq.moduleIds || []);
    // the dialog has no model fields: line items are added under the orientation picked in the filter bar
    const canAdd: boolean = isCascadeComplete(filterCascade) && !IsStringNullEmptyOrUndefined(filterCascade.orientation);

    const selectedOrientation: BOMOrientationDDLEntity | undefined = filterOrientations.find((item) => item.id === filterCascade.orientation);
    const activeModelCount: number = selectedOrientation ? selectedOrientation.modelCount : 0;

    const pinnedCodes: string[] = pinnedCSProductCodes !== null ? pinnedCSProductCodes : csProductCodes;
    const orderedCSMasterDDL: CSMasterDDLEntity[] = useMemo(() => sortSelectedFirst(csMasterDDL, pinnedCodes, (item) => item.csProductCode), [csMasterDDL, pinnedCodes]);

    useEffect(() => { return () => { toast.dismiss(); }; }, []);

    useEffect(() => {
        fetchSystems();
        fetchCSMasterDDL();
        fetchFilterOptions();
        fetchFormulaVariables();
        fetchUOMDDL();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (canList) {
            fetchRecords();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterReq, filterCascade]);

    useEffect(() => {
        loadOrientations(filterCascade, setFilterOrientations, setOrientationsLoading);
        fetchBOMCSCodesDDL(filterCascade);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCascade.systemId, filterCascade.subSystemId, filterCascade.systemTypeId, filterCascade.orientation]);

    useEffect(() => {
        loadOrientations(copyCascade, setCopyOrientations, setCopyOrientationsLoading);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [copyCascade.systemId, copyCascade.subSystemId, copyCascade.systemTypeId]);

    const startLoading = (): void => setLoadingCount((count) => count + 1);
    const stopLoading = (): void => setLoadingCount((count) => Math.max(0, count - 1));

    const fetchSystems = async (): Promise<void> => {
        startLoading();
        const resp: AxiosResponse<APIResponseEntity> = await APIGetSystemDDL('', '', '', false);
        stopLoading();
        if (isOkResponse(resp) && resp.data.data) {
            setSystems(resp.data.data.data || []);
        } else if (resp && resp.data) {
            toast.error(resp.data.message, { ...toastOptObj });
        }
    };

    const fetchBOMCSCodesDDL = async (cascade?: BOMModelCascadeEntity): Promise<void> => {
        try {
            const resp: AxiosResponse<APIResponseEntity> = await APIToGetBOMCSCodesDDL(cascade);
            if (isOkResponse(resp) && resp.data.data) {
                const list: BOMCSCodeDDLEntity[] = resp.data.data.data || [];
                setCSCodeOptions(list);
            }
        } catch (error) {
            console.error('Error fetching BOM CS codes DDL:', error);
        }
    };

    const fetchReferenceModulesDDL = async (moduleTypes: string[], showLoader: boolean = true): Promise<void> => {
        try {
            if (IsNullOrEmptyArray(moduleTypes)) {
                return;
            }
            const missingTypes = moduleTypes.filter((t) => !moduleDDLMap[t]);
            if (IsNullOrEmptyArray(missingTypes)) {
                return;
            }
            if (showLoader) {
                startLoading();
            }
            const resp: AxiosResponse<APIResponseEntity> = await APIToGetBOMReferenceModulesDDL({ moduleTypes: missingTypes });
            if (isOkResponse(resp) && resp.data.data) {
                const mapData = resp.data.data as { [key: string]: { _id: string; name: string }[] };
                const formatted: { [key: string]: DDLEntity[] } = {};
                Object.keys(mapData).forEach((key) => {
                    const list = (mapData[key] || []).map((item) => ({ id: item._id, name: item.name })).sort(byName);
                    if (!IsNullOrEmptyArray(list)) {
                        formatted[key] = list;
                    }
                });
                setModuleDDLMap((prev) => ({ ...prev, ...formatted }));
            }
        } catch (error) {
            console.error('Error fetching reference modules DDL:', error);
        } finally {
            if (showLoader) {
                stopLoading();
            }
        }
    };

    const fetchCSMasterDDL = async (): Promise<void> => {
        const resp: AxiosResponse<APIResponseEntity> = await APICSMasterDDL();
        if (isOkResponse(resp) && resp.data.data) {
            setCSMasterDDL(resp.data.data.data || []);
        } else if (resp && resp.data) {
            toast.error(resp.data.message, { ...toastOptObj });
        }
    };

    /** CS types in use on some CS master and every active frame color: the two filter bar pick lists */
    const fetchFilterOptions = async (): Promise<void> => {
        const [typeResp, frameColorResp] = await Promise.all([APIToGetMappedCSMasterTypes(), APIFrameColorsDDL()]);
        setCSMasterTypeOptions(isOkResponse(typeResp) && typeResp.data.data ? typeResp.data.data.data || [] : []);
        setFrameColorOptions(isOkResponse(frameColorResp) && frameColorResp.data.data ? frameColorResp.data.data.data || [] : []);
    };

    /** the variables and functions a formula may use, with how each value is found, as the backend defines them */
    const fetchFormulaVariables = async (): Promise<void> => {
        const resp: AxiosResponse<APIResponseEntity> = await APIToGetBOMFormulaVariables();
        if (isOkResponse(resp) && resp.data.data) {
            setFormulaVariables(resp.data.data.variables || []);
            setFormulaFunctions(resp.data.data.functions || []);
        } else if (resp && resp.data) {
            toast.error(resp.data.message, { ...toastOptObj });
        }
    };

    const fetchUOMDDL = async (): Promise<void> => {
        try {
            const resp: AxiosResponse<APIResponseEntity> = await APIToGetBomMappingUOMDDL();
            if (isOkResponse(resp) && resp.data.data) {
                const list = resp.data.data.data || resp.data.data || [];
                setUomOptions(list.map((item: any) => ({
                    id: item.id,
                    name: item.name
                })));
            }
        } catch (error) {
            console.error('Error fetching UOM DDL:', error);
        }
    };

    const loadOrientations = async (
        cascade: BOMModelCascadeEntity,
        setOrientations: (items: BOMOrientationDDLEntity[]) => void,
        setLoading: (loading: boolean) => void
    ): Promise<void> => {
        if (!isCascadeComplete(cascade)) {
            setOrientations([]);
            return;
        }
        setLoading(true);
        const resp: AxiosResponse<APIResponseEntity> = await APIToGetBOMOrientationDDL({
            systemId: cascade.systemId, subSystemId: cascade.subSystemId, systemTypeId: cascade.systemTypeId
        });
        setLoading(false);
        if (isOkResponse(resp) && resp.data.data) {
            setOrientations(resp.data.data.data || []);
        } else {
            setOrientations([]);
            if (resp && resp.data) {
                toast.error(resp.data.message, { ...toastOptObj });
            }
        }
    };

    const fetchRecords = async (): Promise<void> => {
        startLoading();
        const resp: AxiosResponse<APIResponseEntity> = await APIToGetBOMModelWiseMappings({ ...filterReq, ...filterCascade });
        stopLoading();
        setHasFetched(true);
        setRecords([]);
        setTotalCount(0);
        if (isOkResponse(resp) && resp.data.data) {
            const paginateResp: PaginateRespEntity = resp.data.data;
            setRecords(paginateResp.data);
            setTotalCount(paginateResp.totalCount);
        } else if (resp && resp.data) {
            toast.error(resp.data.message, { ...toastOptObj });
        }
    };

    const fetchBOMMappingCsvDetails = async (): Promise<void> => {
        startLoading();
        const resp: AxiosResponse<APIResponseEntity> = await APIToExportBOMModelWiseMappings();
        stopLoading();
        if (isOkResponse(resp) && resp.data.data) {
            const excelUrl = resp.data.data.xlsxUrl;
            if (excelUrl && !IsStringNullEmptyOrUndefined(excelUrl)) {
                const fileName: string = excelUrl.split("/").pop() || "export-bom-mapping.xlsx";
                await downloadCsvFile(excelUrl, fileName);
            }
        } else if (resp && resp.data) {
            toast.error(resp.data.message, { ...toastOptObj });
        }
    };

    const onFilterCascadeChange = (next: BOMModelCascadeEntity): void => {
        setFilterCascade(next);
        setFilterReq({ ...filterReq, pageNumber: 0 });
    };

    const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        const searchText: string = IsStringNullEmptyOrUndefined(searchKeyword) ? '' : searchKeyword.trim();
        if (event.key === 'Enter' && (IsStringNullEmptyOrUndefined(searchText) || searchText.length >= 3)) {
            setFilterReq({ ...filterReq, searchText, pageNumber: 0 });
        }
    };

    const clearSearch = (): void => {
        setSearchKeyword('');
        if (!IsStringNullEmptyOrUndefined(filterReq.searchText || '')) {
            setFilterReq({ ...filterReq, searchText: '', pageNumber: 0 });
        }
    };

    const activeModuleTypes: string[] = useMemo(() => {
        return (filterReq.references || []).filter((r) => r !== 'MANUAL' && Object.prototype.hasOwnProperty.call(MODULE_TYPE_LABELS, r));
    }, [filterReq.references]);

    useEffect(() => {
        if (!IsNullOrEmptyArray(activeModuleTypes)) {
            fetchReferenceModulesDDL(activeModuleTypes);
        } else {
            setPendingModuleSelections({});
        }
    }, [activeModuleTypes]);

    const onModulePicksChange = (moduleType: string, ids: string[]): void => {
        setPendingModuleSelections((prev) => ({
            ...prev,
            [moduleType]: ids
        }));
    };

    const visibleModuleTypes = useMemo(() => {
        return activeModuleTypes.filter((mType) => !IsNullOrEmptyArray(moduleDDLMap[mType]));
    }, [activeModuleTypes, moduleDDLMap]);

    const flatPendingModuleIds = useMemo(() => {
        return visibleModuleTypes.flatMap((mType) => pendingModuleSelections[mType] || []);
    }, [visibleModuleTypes, pendingModuleSelections]);

    const resetFilters = (): void => {
        setSearchKeyword('');
        setFilterCascade(EMPTY_CASCADE);
        setPendingCSProductCodes([]);
        setPendingCSMasterTypeIds([]);
        setPendingFrameColorIds([]);
        setPendingReferences([]);
        setPendingModuleSelections({});
        setModuleDDLMap({});
        setFilterReq((prev) => ({
            ...DEFAULT_BOM_FILTER_REQ,
            pageSize: prev.pageSize
        }));
    };

    const isDropdownApplyRef = useRef(false);

    const isDropdownApplyDisabled = (): boolean => {
        const csChanged = JSON.stringify([...pendingCSProductCodes].sort()) !== JSON.stringify([...(filterReq.csProductCodes || [])].sort());
        if (csChanged) return false;
        const modulesChanged = JSON.stringify([...flatPendingModuleIds].sort()) !== JSON.stringify([...(filterReq.moduleIds || [])].sort());
        if (modulesChanged) return false;
        return (
            JSON.stringify([...pendingCSMasterTypeIds].sort()) === JSON.stringify([...(filterReq.csMasterTypeIds || [])].sort()) &&
            JSON.stringify([...pendingFrameColorIds].sort()) === JSON.stringify([...(filterReq.frameColorIds || [])].sort()) &&
            JSON.stringify([...pendingReferences].sort()) === JSON.stringify([...(filterReq.references || [])].sort())
        );
    };

    const handleApplyDropdownFilters = (hide: () => void): void => {
        isDropdownApplyRef.current = true;
        setRecords([]);
        setTotalCount(0);
        setFilterReq((prev) => ({
            ...prev,
            csProductCodes: pendingCSProductCodes,
            csMasterTypeIds: pendingCSMasterTypeIds,
            frameColorIds: pendingFrameColorIds,
            references: pendingReferences,
            moduleIds: flatPendingModuleIds,
            pageNumber: 0
        }));
        hide();
    };

    const handleDropdownHide = (): void => {
        if (isDropdownApplyRef.current) {
            isDropdownApplyRef.current = false;
            return;
        }
        setPendingCSProductCodes(filterReq.csProductCodes || []);
        setPendingCSMasterTypeIds(filterReq.csMasterTypeIds || []);
        setPendingFrameColorIds(filterReq.frameColorIds || []);
        setPendingReferences(filterReq.references || []);
    };

    const dropdownApplyFooter = (_props: any, hide: () => void) => (
        <div className="flex justify-between p-2">
            <button
                type="button"
                className="p-button p-component p-button-sm"
                onClick={() => handleApplyDropdownFilters(hide)}
                disabled={isDropdownApplyDisabled()}
            >
                Apply
            </button>
        </div>
    );

    const onPicksChange = (field: 'csProductCodes' | 'csMasterTypeIds' | 'frameColorIds' | 'references', picks: string[]): void => {
        if (field === 'csProductCodes') { setPendingCSProductCodes(picks); return; }
        if (field === 'csMasterTypeIds') {
            setPendingCSMasterTypeIds(picks);
        } else if (field === 'frameColorIds') {
            setPendingFrameColorIds(picks);
        } else {
            setPendingReferences(picks);
            const moduleTypes = (picks || []).filter((r) => r !== 'MANUAL' && Object.prototype.hasOwnProperty.call(MODULE_TYPE_LABELS, r));
            if (!IsNullOrEmptyArray(moduleTypes)) {
                fetchReferenceModulesDDL(moduleTypes, false);
            }
        }
    };

    const onPageChange = (event: PaginatorPageChangeEvent): void => {
        setFilterReq({ ...filterReq, pageSize: event.rows, pageNumber: event.page });
    };

    const onSort = (event: DataTableSortEvent): void => {
        setFilterReq({ ...filterReq, sortColumn: event.sortField, sortOrder: String(event.sortOrder), pageNumber: 0 });
    };

    const csMasterError: string = IsNullOrEmptyArray(csProductCodes) ? (isEdit ? 'Please select CS master' : 'Please select at least one CS master') : '';
    const isFormulaSyntaxError: boolean = !IsStringNullEmptyOrUndefined(previewError);
    // on add the formula may be left empty: every picked CS master then maps with its default formula
    const formulaError: string = IsStringNullEmptyOrUndefined(QtyFormula.trim())
        ? (isEdit ? 'Please enter the quantity formula' : '')
        : (formulaVerdict.parseError || previewError);
    const uomError: string = IsStringNullEmptyOrUndefined(selectedUomId) || selectedUomId.trim() === '' || selectedUomId === '-' ? 'Please select UOM' : '';

    /** "system › sub system › system type · orientation" of the filter bar picks, shown under the dialog title */
    const filterSummary = (): string => {
        const system = systems.find((item) => item.systemId === filterCascade.systemId);
        const subSystem = system ? system.mappings.find((item) => item.subSystemId === filterCascade.subSystemId) : undefined;
        const systemType = subSystem ? subSystem.systemTypesMapping.find((item) => item.systemTypeId === filterCascade.systemTypeId) : undefined;
        const path: string = [system ? system.systemName : '', subSystem ? subSystem.subSystemName : '', systemType ? systemType.systemTypeName : '']
            .filter((part) => !IsStringNullEmptyOrUndefined(part)).join(' › ');
        return selectedOrientation ? path + ' · ' + orientationOptionLabel(selectedOrientation) : path;
    };

    const rowSummary = (row: BOMModelWiseMappingDetailsEntity): string => {
        const path: string = [row.systemName, row.subSystemName, row.systemTypeName].filter((part) => !IsStringNullEmptyOrUndefined(part)).join(' › ');
        const model: string = [row.modelCode, row.subOrientation].filter((part) => !IsStringNullEmptyOrUndefined(part)).join(' · ');
        return [path, model].filter((part) => !IsStringNullEmptyOrUndefined(part)).join(' · ');
    };

    const openCreateDialog = (): void => {
        if (!canAdd) {
            return;
        }
        setEditRecordId('');
        setEditLinked(false);
        setEditHasFormula(false);
        setCSProductCodes([]);
        setSelectedUomId('');
        setDialogContext(filterSummary());
        setRuleItemName('');
        setRuleItemCode('');
        setQtyFormula('');
        setIsSubmitted(false);
        setIsDialogOpen(true);
    };

    const openEditDialog = (row: BOMModelWiseMappingDetailsEntity): void => {
        setEditRecordId(row.id);
        setEditLinked(row.source === BOMLineItemSourceEnum.MODEL_MAPPING);
        // a line following its CS master's default holds no formula of its own, so its CS master may still change
        setEditHasFormula(!IsStringNullEmptyOrUndefined(row.formula || '') && !row.isDefaultFormula);
        setCSProductCodes(IsStringNullEmptyOrUndefined(row.csProductCode) ? [] : [row.csProductCode]);
        const rawUomId: string = row.bomMappingUomId || '';
        setSelectedUomId(rawUomId === '-' ? '' : rawUomId);
        setDialogContext(rowSummary(row));
        setRuleItemName(row.csMasterDescription || '');
        setRuleItemCode(row.csMasterProductCode || '');
        setQtyFormula(row.formula || '');
        setIsSubmitted(false);
        setIsDialogOpen(true);
    };

    const closeDialog = (): void => {
        setIsDialogOpen(false);
        setEditRecordId('');
        setEditLinked(false);
        setEditHasFormula(false);
        setCSProductCodes([]);
        setSelectedUomId('');
        setPinnedCSProductCodes(null);
        setRuleItemCode('');
        setRuleItemName('');
        setIsSubmitted(false);
    };

    const cascadeKey = (cascade: BOMModelCascadeEntity): string => {
        return [cascade.systemId, cascade.subSystemId, cascade.systemTypeId].join('|');
    };

    const copyTargetError: string = !isCascadeComplete(copyCascade) || IsNullOrEmptyArray(copyTargetOrientations)
        ? 'Please select target system, sub system, system type and at least one orientation'
        : cascadeKey(copyCascade) === cascadeKey(filterCascade) && copyTargetOrientations.indexOf(filterCascade.orientation) !== -1
            ? 'Target orientations must not include the source orientation'
            : '';

    const copyItemsError: string = IsNullOrEmptyArray(copyCSProductCodes) ? 'Please select at least one line item to copy' : '';

    /** every line item live under the source orientation, all pre-selected; fetched whole rather than from the paginated listing */
    const loadCopyItems = async (source: BOMModelCascadeEntity): Promise<void> => {
        setCopyItemsLoading(true);
        const resp: AxiosResponse<APIResponseEntity> = await APIToGetBOMCopyItems(source);
        setCopyItemsLoading(false);
        const items: CSMasterDDLEntity[] = isOkResponse(resp) && resp.data.data ? resp.data.data.data || [] : [];
        setCopyItems(items);
        setCopyCSProductCodes(items.map((item) => item.csProductCode));
        if (!isOkResponse(resp) && resp && resp.data) {
            toast.error(resp.data.message, { ...toastOptObj });
        }
    };

    const openCopyDialog = (): void => {
        if (!canAdd || !canCopy || totalCount === 0) {
            return;
        }
        setCopyCascade(EMPTY_CASCADE);
        setCopyTargetOrientations([]);
        setCopyItems([]);
        setCopyCSProductCodes([]);
        setDialogContext(filterSummary());
        setIsCopySubmitted(false);
        setIsCopyDialogOpen(true);
        loadCopyItems(filterCascade);
    };

    const closeCopyDialog = (): void => {
        setIsCopyDialogOpen(false);
        setCopyCascade(EMPTY_CASCADE);
        setCopyTargetOrientations([]);
        setCopyItems([]);
        setCopyCSProductCodes([]);
        setIsCopySubmitted(false);
    };

    const copyLineItems = async (): Promise<void> => {
        setIsCopySubmitted(true);
        if (copyItemsLoading || !IsStringNullEmptyOrUndefined(copyItemsError) || !IsStringNullEmptyOrUndefined(copyTargetError)) {
            return;
        }
        startLoading();
        const resp: AxiosResponse<APIResponseEntity> = await APIToCopyBOMModelWiseMappings({
            source: filterCascade,
            target: { systemId: copyCascade.systemId, subSystemId: copyCascade.subSystemId, systemTypeId: copyCascade.systemTypeId, orientations: copyTargetOrientations },
            csProductCodes: copyCSProductCodes
        });
        stopLoading();
        if (isOkResponse(resp)) {
            toast.success(resp.data.message, { ...toastOptObj });
            // the listing moves to the target so the copied line items are in view: the one orientation, or the whole combination when several
            const target: BOMModelCascadeEntity = { ...copyCascade, orientation: copyTargetOrientations.length === 1 ? copyTargetOrientations[0] : '' };
            closeCopyDialog();
            onFilterCascadeChange(target);
        } else if (resp && resp.data) {
            toast.error(resp.data.message, { ...toastOptObj });
        }
    };

    const saveLineItems = async (): Promise<void> => {
        setIsSubmitted(true);
        if (!IsStringNullEmptyOrUndefined(csMasterError) || !IsStringNullEmptyOrUndefined(formulaError) || !IsStringNullEmptyOrUndefined(uomError)) {
            return;
        }
        const formula: string = QtyFormula.trim();
        startLoading();
        const resp: AxiosResponse<APIResponseEntity> = isEdit
            ? await APIToUpdateBOMModelWiseMapping(editRecordId, { csProductCode: csProductCodes[0], formula, bomMappingUomId: selectedUomId })
            : await APIToCreateBOMModelWiseMapping({ ...filterCascade, csProductCodes, formula, bomMappingUomId: selectedUomId });
        stopLoading();
        if (isOkResponse(resp)) {
            toast.success(resp.data.message, { ...toastOptObj });
            closeDialog();
            fetchRecords();
        } else if (resp && resp.data) {
            toast.error(resp.data.message, { ...toastOptObj });
        }
    };

    const deleteLineItem = async (id: string): Promise<void> => {
        startLoading();
        const resp: AxiosResponse<APIResponseEntity> = await APIToDeleteBOMModelWiseMapping(id);
        stopLoading();
        if (isOkResponse(resp)) {
            toast.success(resp.data.message, { ...toastOptObj });
            const isLastRowOfPage: boolean = records.length === 1 && filterReq.pageNumber > 0;
            if (isLastRowOfPage) {
                setFilterReq({ ...filterReq, pageNumber: filterReq.pageNumber - 1 });
            } else {
                fetchRecords();
            }
        } else if (resp && resp.data) {
            toast.error(resp.data.message, { ...toastOptObj });
        }
    };

    const createdAtBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        return <span>{IsStringNullEmptyOrUndefined(row.createdAtIST) ? '-' : row.createdAtIST}</span>;
    };

    const modelBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        const path: string = [row.systemName, row.subSystemName, row.systemTypeName].filter((part) => !IsStringNullEmptyOrUndefined(part)).join(' › ');
        return <span className="bom-model-code">{IsStringNullEmptyOrUndefined(path) ? '-' : path}</span>;
    };

    const orientationBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        const orientation: string = IsStringNullEmptyOrUndefined(row.subOrientation) || row.subOrientation === row.orientation
            ? row.orientation
            : row.orientation + ' / ' + row.subOrientation;
        return <span>{IsStringNullEmptyOrUndefined(orientation) ? '-' : orientation}</span>;
    };

    const imageBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        return IsStringNullEmptyOrUndefined(row.csMasterImageUrl)
            ? <span className="bom-cs-thumb bom-cs-thumb-empty"><i className="pi pi-image"></i></span>
            : <Image src={row.csMasterImageUrl} zoomSrc={row.csMasterImageUrl} alt={row.csMasterProductCode} width="40" height="40" preview className="bom-cs-thumb-wrap" imageClassName="bom-cs-thumb" />;
    };

    const csMasterBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        return <span className="bom-cs-code">{IsStringNullEmptyOrUndefined(row.csMasterProductCode) ? '-' : row.csMasterProductCode}</span>;
    };

    /** CSMasterModuleTypeEnum values of the backend, labelled for the Reference column */
    const linkedModuleLabels: { [moduleType: string]: string } = { handles: 'Handle', locks: 'Lock', glassFinishes: 'Finish', glassVariants: 'Variant' };

    const referenceBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        if (row.source !== BOMLineItemSourceEnum.MODEL_MAPPING) {
            return <span className="bom-empty-hint">Manual</span>;
        }
        return (
            <div className="bom-ref" title="Linked to the model's CS master mappings; change it on the handle, lock, finish or variant">
                <span className="bom-cs-source">{row.moduleType && linkedModuleLabels[row.moduleType] ? linkedModuleLabels[row.moduleType] : 'Linked'}</span>
                <span className="bom-ref-name">{IsStringNullEmptyOrUndefined(row.linkedModuleName) ? '-' : row.linkedModuleName}</span>
                {!IsStringNullEmptyOrUndefined(row.linkedFrameColorName) && <span className="bom-ref-color">{row.linkedFrameColorName}</span>}
            </div>
        );
    };

    const descriptionBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        return <span className="bom-cs-desc-cell">{IsStringNullEmptyOrUndefined(row.csMasterDescription) ? '-' : row.csMasterDescription}</span>;
    };

    const formulaBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        return (
            <span className="font-weight-medium" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {IsStringNullEmptyOrUndefined(row.formula || '') ? '-' : row.formula}
                {row.isDefaultFormula && <span className="bom-cs-source ml-1" title="Carries the default formula of its CS master">Default</span>}
            </span>
        );
    };

    const uomBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        return <span>{IsStringNullEmptyOrUndefined(row.bomMappingUomName) ? '-' : row.bomMappingUomName}</span>;
    };

    const csMasterTypeBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        return <span>{IsStringNullEmptyOrUndefined(row.csMasterType) ? '-' : row.csMasterType}</span>;
    };

    const frameColorBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        return <span>{IsStringNullEmptyOrUndefined(row.frameColor) ? '-' : row.frameColor}</span>;
    };

    /** a linked line can have its formula edited but not be deleted; a manual line both */
    const actionBodyTemplate = (row: BOMModelWiseMappingDetailsEntity) => {
        const isLinked: boolean = row.source === BOMLineItemSourceEnum.MODEL_MAPPING;
        if (!canUpdate && (isLinked || !canDelete)) {
            return <span className="bom-empty-hint">-</span>;
        }
        return (
            <ul className="tableIcon cursor-pointer action-icon-template-w">
                {canUpdate &&
                    <li data-toggle="tooltip" title={isLinked ? 'Edit formula' : 'Edit'}>
                        <img src="assets/images/edit-icon.svg" alt="Edit" onClick={() => openEditDialog(row)} />
                    </li>}
                {canDelete && !isLinked &&
                    <li data-toggle="tooltip" title="Delete">
                        <DeleteConfirmationDialog isAccepted={() => deleteLineItem(row.id)} />
                    </li>}
            </ul>
        );
    };

    const csMasterItemTemplate = (option: CSMasterDDLEntity) => {
        return (
            <div className="bom-cs-dd-item">
                <span className="bom-cs-code">{option.name}</span>
                <span className="bom-cs-desc" title={option.description}>{option.description}</span>
            </div>
        );
    };

    const dialogHeader = (
        <div>
            <div className="bom-dialog-title">{isEdit ? 'Edit Line Item' : 'Add Line Items'}</div>
            <div className="bom-dialog-sub">{dialogContext}</div>
        </div>
    );

    const dialogFooter = (
        <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-cancel text-dark m-0" onClick={closeDialog}>Cancel</button>
            {((isEdit && canUpdate) || (!isEdit && canCreate)) &&
                <button
                    type="button"
                    className="btn btn-primary font-weight-medium m-0"
                    disabled={isFormulaSyntaxError}
                    onClick={saveLineItems}
                >
                    {isEdit ? 'Update Line Item' : 'Save Line Items'}
                </button>}
        </div>
    );

    const copyDialogHeader = (
        <div>
            <div className="bom-dialog-title">Copy Line Items</div>
            <div className="bom-dialog-sub">From {dialogContext}</div>
        </div>
    );

    const copyDialogFooter = (
        <div className="d-flex gap-2">
            {canCopy &&
                <button type="button" className="btn btn-primary font-weight-medium m-0" onClick={copyLineItems}>Copy Line Items</button>}
            <button type="button" className="btn btn-cancel text-dark m-0" onClick={closeCopyDialog}>Cancel</button>
        </div>
    );

    return (
        <>
            <Loader isLoading={loadingCount > 0} />

            <div className="content-wrapper bom-page">
                <div className="card card-rounded">
                    <div className="card-body">
                        <div className="row align-items-center mb-3">
                            <div className="col-md-6">
                                <h4 className="card-title mb-0">BOM Mapping</h4>
                                <div className="bom-header-sub">CS masters mapped per model</div>
                            </div>
                            <div className="col-md-6 d-flex flex-column align-items-end">
                                {(canExport || canCopy || canCreate) &&
                                    <div className="d-flex gap-2">
                                        {canExport &&
                                            <button
                                                type="button"
                                                className="btn btn-success font-weight-medium"
                                                onClick={fetchBOMMappingCsvDetails}
                                            >
                                                Export BOM Mapping
                                            </button>}
                                        {canCopy &&
                                            <button
                                                type="button"
                                                className="btn btn-primary font-weight-medium"
                                                disabled={!canAdd || totalCount === 0}
                                                title={!canAdd
                                                    ? 'Select the system, sub system, system type and orientation to copy from first'
                                                    : totalCount === 0 ? 'No line items to copy under this orientation' : ''}
                                                onClick={openCopyDialog}
                                            >
                                                Copy Items
                                            </button>}
                                        {canCreate &&
                                            <button
                                                type="button"
                                                className="btn btn-success font-weight-medium"
                                                disabled={!canAdd}
                                                title={canAdd ? '' : 'Select system, sub system, system type and orientation first'}
                                                onClick={openCreateDialog}
                                            >
                                                <i className="pi pi-plus me-1"></i> Add Line Items
                                            </button>}
                                    </div>}
                                <span className="resetFilters text-primary cursor-pointer mt-2 bom-reset-link" onClick={resetFilters}>
                                    Reset Filters
                                </span>
                            </div>
                        </div>

                        {!canList ?
                            <div className="alert alert-danger" role="alert">
                                You don't have permission to view BOM mapping listing
                            </div>
                            :
                            <>
                                <div className="row bom-filter-bar">
                                    <CascadeFields
                                        value={filterCascade}
                                        systems={systems}
                                        orientations={filterOrientations}
                                        orientationsLoading={orientationsLoading}
                                        onChange={onFilterCascadeChange}
                                        fieldClassName="col-12 bom-col-5 mb-3 form-group bom-filter-field"
                                    />
                                    <div className="col-12 bom-col-5 mb-3 form-group bom-filter-field">
                                        <label>CS Code</label>
                                        <MultiSelect
                                            value={pendingCSProductCodes}
                                            options={csCodeOptions}
                                            optionLabel="csCode"
                                            optionValue="csProductCode"
                                            placeholder="Select CS codes"
                                            maxSelectedLabels={2}
                                            selectedItemsLabel="{0} selected"
                                            filter
                                            resetFilterOnHide
                                            emptyFilterMessage={emptyMultiSelectSearchMessage}
                                            onChange={(e) => onPicksChange('csProductCodes', e.value || [])}
                                            panelFooterTemplate={dropdownApplyFooter}
                                            onHide={handleDropdownHide}
                                            appendTo="self"
                                        />
                                    </div>
                                    <div className="col-12 bom-col-5 mb-3 form-group bom-filter-field">
                                        <label>CS TYPE</label>
                                        <MultiSelect
                                            value={pendingCSMasterTypeIds}
                                            options={csMasterTypeOptions}
                                            optionLabel="type"
                                            optionValue="_id"
                                            placeholder="Select CS types"
                                            maxSelectedLabels={1}
                                            selectedItemsLabel="{0} selected"
                                            filter
                                            resetFilterOnHide
                                            emptyFilterMessage={emptyMultiSelectSearchMessage}
                                            onChange={(e) => onPicksChange('csMasterTypeIds', e.value || [])}
                                            panelFooterTemplate={dropdownApplyFooter}
                                            onHide={handleDropdownHide}
                                            appendTo="self"
                                        />
                                    </div>
                                    <div className="col-12 bom-col-5 mb-3 form-group bom-filter-field">
                                        <label>FRAME COLOR</label>
                                        <MultiSelect
                                            value={pendingFrameColorIds}
                                            options={frameColorOptions}
                                            optionLabel="name"
                                            optionValue="id"
                                            placeholder="Select frame colors"
                                            maxSelectedLabels={1}
                                            selectedItemsLabel="{0} selected"
                                            filter
                                            resetFilterOnHide
                                            emptyFilterMessage={emptyMultiSelectSearchMessage}
                                            onChange={(e) => onPicksChange('frameColorIds', e.value || [])}
                                            panelFooterTemplate={dropdownApplyFooter}
                                            onHide={handleDropdownHide}
                                            appendTo="self"
                                        />
                                    </div>
                                    <div className="col-12 bom-col-5 mb-3 form-group bom-filter-field">
                                        <label>REFERENCE</label>
                                        <MultiSelect
                                            value={pendingReferences}
                                            options={BOM_REFERENCE_OPTIONS}
                                            optionLabel="name"
                                            optionValue="id"
                                            placeholder="Select references"
                                            maxSelectedLabels={1}
                                            selectedItemsLabel="{0} selected"
                                            filter
                                            resetFilterOnHide
                                            emptyFilterMessage={emptyMultiSelectSearchMessage}
                                            onChange={(e) => onPicksChange('references', e.value || [])}
                                            panelFooterTemplate={dropdownApplyFooter}
                                            onHide={handleDropdownHide}
                                            appendTo="self"
                                        />
                                    </div>
                                    {visibleModuleTypes.map((moduleType) => (
                                        <div key={moduleType} className="col-12 bom-col-5 mb-3 form-group bom-filter-field">
                                            <label>{MODULE_TYPE_LABELS[moduleType] || moduleType.toUpperCase()}</label>
                                            <MultiSelect
                                                value={pendingModuleSelections[moduleType] || []}
                                                options={moduleDDLMap[moduleType] || []}
                                                optionLabel="name"
                                                optionValue="id"
                                                placeholder={`Select ${(MODULE_TYPE_LABELS[moduleType] || moduleType).toLowerCase()}s`}
                                                maxSelectedLabels={1}
                                                selectedItemsLabel="{0} selected"
                                                filter
                                                resetFilterOnHide
                                                emptyFilterMessage={emptyMultiSelectSearchMessage}
                                                onChange={(e) => onModulePicksChange(moduleType, e.value || [])}
                                                panelFooterTemplate={dropdownApplyFooter}
                                                onHide={handleDropdownHide}
                                                appendTo="self"
                                            />
                                        </div>
                                    ))}
                                    <div className="col-12 bom-col-5 mb-3 form-group bom-filter-field bom-search-field">
                                        <label>SEARCH</label>
                                        <div className="input-group search-grp">
                                            <img src="assets/images/search.svg" alt="search" className="input-group-text searchIptGrpTxt" />
                                            <input
                                                type="text"
                                                className="form-control form-control-lg searchInput"
                                                placeholder="Search by CS code, description, orientation or model code"
                                                value={searchKeyword}
                                                onChange={(e) => setSearchKeyword(e.target.value)}
                                                onKeyDown={onSearchKeyDown}
                                            />
                                            {!IsStringNullEmptyOrUndefined(searchKeyword) &&
                                                <button type="button" className="btn btn-cancel text-dark search-button-clear" onClick={clearSearch}>Clear</button>}
                                        </div>
                                    </div>
                                </div>

                                <div className="bom-table-count">{totalCount} total</div>

                                <div className="bom-table">
                                    <DataTable
                                        value={records}
                                        lazy
                                        dataKey="id"
                                        sortField={filterReq.sortColumn}
                                        sortOrder={Number(filterReq.sortOrder) === 1 ? 1 : -1}
                                        onSort={onSort}
                                        emptyMessage="No BOM line items found"
                                    >
                                        <Column field="modelCode" header="Model" body={modelBodyTemplate} sortable style={{ minWidth: '240px' }} />
                                        <Column field="orientation" header="Orientation" body={orientationBodyTemplate} sortable style={{ minWidth: '140px' }} />
                                        <Column header="Image" body={imageBodyTemplate} style={{ width: '80px' }} />
                                        <Column field="csMasterProductCode" header="CS Code" body={csMasterBodyTemplate} sortable style={{ minWidth: '150px' }} />
                                        <Column field="csMasterDescription" header="Description" body={descriptionBodyTemplate} sortable style={{ minWidth: '280px' }} />
                                        <Column field="formula" header="Formula" body={formulaBodyTemplate} sortable style={{ minWidth: '160px' }} />
                                        <Column field="uom" header="UOM" body={uomBodyTemplate} style={{ minWidth: '120px' }} />
                                        <Column header="Reference" body={referenceBodyTemplate} style={{ minWidth: '220px' }} />
                                        <Column field="csMasterType" header="CS Master Type" body={csMasterTypeBodyTemplate} sortable style={{ minWidth: '180px' }} />
                                        <Column field="frameColor" header="Frame Color" body={frameColorBodyTemplate} sortable style={{ minWidth: '160px' }} />
                                        <Column field="createdAt" header="Created At" body={createdAtBodyTemplate} sortable style={{ minWidth: '150px' }} />
                                        {(canUpdate || canDelete) &&
                                            <Column header="Action" body={actionBodyTemplate} style={{ width: '100px', textAlign: 'center' }} />}
                                    </DataTable>
                                    {hasFetched && loadingCount === 0 && IsNullOrEmptyArray(records) &&
                                        <div className="bom-empty">
                                            <i className="pi pi-inbox bom-empty-icon"></i>
                                            <div className="bom-empty-title">No BOM line items found</div>
                                            <div className="bom-empty-hint">
                                                {isFiltered ? 'Try another orientation, filter or search.' : 'Select a system, sub system, system type and orientation to add line items.'}
                                            </div>
                                        </div>}
                                    {totalCount > 0 &&
                                        <PrimePaginator
                                            onPageChange={onPageChange}
                                            pageNumber={filterReq.pageNumber}
                                            pageSize={filterReq.pageSize}
                                            totalRecords={totalCount}
                                        />}
                                </div>
                            </>}
                    </div>
                </div>
            </div>

            <Dialog
                visible={isDialogOpen}
                header={dialogHeader}
                footer={dialogFooter}
                className="bom-dialog"
                modal
                draggable={false}
                resizable={false}
                style={{ width: '960px', maxWidth: '96vw' }}
                onHide={closeDialog}
            >
                {/* Row 1: Line Item (Left) | Item Code & Description (Right) */}
                <div className="row mb-3 align-items-center">
                    <div className="col-12 col-md-6 mb-2 mb-md-0">
                        {/* Line Item */}
                        <div className="form-group mb-0">
                            <label className="text-muted small font-weight-bold d-flex align-items-center mb-1">
                                <span>LINE ITEM (CS MASTER)</span>
                                <sup className="text-danger font-weight-bold ml-1">*</sup>
                            </label>
                            {isEdit ? (
                                <Dropdown
                                    value={IsNullOrEmptyArray(csProductCodes) ? null : csProductCodes[0]}
                                    options={csMasterDDL}
                                    optionLabel="name"
                                    optionValue="csProductCode"
                                    placeholder="Select CS master"
                                    filter
                                    filterBy="name,description"
                                    resetFilterOnHide
                                    scrollHeight="220px"
                                    panelClassName="bom-cs-dd-panel"
                                    virtualScrollerOptions={{ itemSize: 60 }}
                                    itemTemplate={csMasterItemTemplate}
                                    emptyFilterMessage={emptyMultiSelectSearchMessage}
                                    disabled={editLinked || editHasFormula}
                                    className={isSubmitted && !IsStringNullEmptyOrUndefined(csMasterError) ? 'p-invalid w-100' : 'w-100'}
                                    onChange={(e) => {
                                        const val = e.value;
                                        setCSProductCodes(val ? [val] : []);
                                        const found = csMasterDDL.find((item) => item.csProductCode === val);
                                        if (found) {
                                            setRuleItemCode(found.name);
                                            setRuleItemName(found.description || '');
                                        }
                                    }}
                                />
                            ) : (
                                <MultiSelect
                                    value={csProductCodes}
                                    options={orderedCSMasterDDL}
                                    optionLabel="name"
                                    optionValue="csProductCode"
                                    placeholder="Select CS masters"
                                    maxSelectedLabels={2}
                                    selectedItemsLabel="{0} selected"
                                    filter
                                    filterBy="name,description"
                                    resetFilterOnHide
                                    scrollHeight="220px"
                                    panelClassName="bom-cs-dd-panel"
                                    virtualScrollerOptions={{ itemSize: 60 }}
                                    itemTemplate={csMasterItemTemplate}
                                    emptyFilterMessage={emptyMultiSelectSearchMessage}
                                    className={isSubmitted && !IsStringNullEmptyOrUndefined(csMasterError) ? 'p-invalid w-100' : 'w-100'}
                                    onShow={() => setPinnedCSProductCodes(csProductCodes)}
                                    onHide={() => setPinnedCSProductCodes(null)}
                                    onChange={(e) => {
                                        const vals = e.value || [];
                                        setCSProductCodes(vals);
                                        if (vals.length > 0) {
                                            const found = csMasterDDL.find((item) => item.csProductCode === vals[0]);
                                            if (found) {
                                                setRuleItemCode(found.name);
                                                setRuleItemName(found.description || '');
                                            }
                                        } else {
                                            setRuleItemCode('');
                                            setRuleItemName('');
                                        }
                                    }}
                                />
                            )}
                            {isSubmitted && !IsStringNullEmptyOrUndefined(csMasterError) && (
                                <small className="text-danger font-weight-bold d-block mt-1">{csMasterError}</small>
                            )}
                            {editLinked && (
                                <small className="text-muted d-block mt-1">Linked to the model's CS master mapping; only the formula can be changed here.</small>
                            )}
                            {!editLinked && editHasFormula && (
                                <small className="text-muted d-block mt-1">A formula is saved on this line, so its CS master cannot be changed.</small>
                            )}
                        </div>
                    </div>

                    <div className="col-12 col-md-6">
                        <div className="d-flex align-items-start" style={{ gap: '20px' }}>
                            <div style={{ flex: '0 0 auto', minWidth: '120px' }}>
                                <label className="text-muted small font-weight-bold d-block mb-1">
                                    ITEM CODE
                                </label>
                                <div
                                    className="font-weight-bold text-dark"
                                    style={{
                                        fontSize: '13px',
                                        wordBreak: 'break-word',
                                        lineHeight: '1.3',
                                    }}
                                >
                                    {ruleItemCode || '-'}
                                </div>
                            </div>
                            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                <label className="text-muted small font-weight-bold d-block mb-1">
                                    DESCRIPTION
                                </label>
                                <div
                                    className="font-weight-medium text-dark"
                                    style={{
                                        fontSize: '13px',
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        lineHeight: '1.3',
                                    }}
                                    title={ruleItemName}
                                >
                                    {ruleItemName || '-'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Rows 2 and 3: UOM above the formula editor shared with the CS master default formula dialog */}
                <BOMFormulaEditor
                    value={QtyFormula}
                    onChange={setQtyFormula}
                    variables={formulaVariables}
                    functions={formulaFunctions}
                    isSubmitted={isSubmitted}
                    error={formulaError}
                    required={isEdit}
                    hint={!isEdit && (
                        <small className="text-muted d-block mt-1">Leave empty to map every picked CS master with its default formula.</small>
                    )}
                    beforeFormula={(
                        <div className="form-group mb-3">
                            <label className="text-muted small font-weight-bold d-flex align-items-center mb-1">
                                <span>UOM</span>
                                <sup className="text-danger font-weight-bold ml-1">*</sup>
                            </label>
                            <Dropdown
                                value={IsStringNullEmptyOrUndefined(selectedUomId) || selectedUomId === '-' ? null : selectedUomId}
                                options={uomOptions}
                                optionLabel="name"
                                optionValue="id"
                                placeholder="Select UOM"
                                filter
                                emptyFilterMessage={emptyMultiSelectSearchMessage}
                                className={isSubmitted && !IsStringNullEmptyOrUndefined(uomError) ? 'p-invalid w-100' : 'w-100'}
                                onChange={(e) => setSelectedUomId(e.value || '')}
                            />
                            {isSubmitted && !IsStringNullEmptyOrUndefined(uomError) && (
                                <small className="text-danger font-weight-bold d-block mt-1">{uomError}</small>
                            )}
                        </div>
                    )}
                />
            </Dialog>

            <Dialog
                visible={isCopyDialogOpen}
                header={copyDialogHeader}
                footer={copyDialogFooter}
                className="bom-dialog"
                modal
                draggable={false}
                resizable={false}
                style={{ width: '640px', maxWidth: '95vw' }}
                onHide={closeCopyDialog}
            >
                <div className="bom-form-block">
                    <div className="bom-form-grid">
                        <div className="form-group mb-0 bom-form-field full">
                            <label>LINE ITEMS TO COPY <sup className="mandatory">*</sup></label>
                            <MultiSelect
                                value={copyCSProductCodes}
                                options={copyItems}
                                optionLabel="name"
                                optionValue="csProductCode"
                                placeholder={copyItemsLoading ? 'Loading line items...' : 'Select line items'}
                                maxSelectedLabels={2}
                                selectedItemsLabel="{0} selected"
                                filter
                                filterBy="name,description"
                                resetFilterOnHide
                                scrollHeight="240px"
                                panelClassName="bom-cs-dd-panel"
                                virtualScrollerOptions={{ itemSize: 60 }}
                                itemTemplate={csMasterItemTemplate}
                                emptyFilterMessage={emptyMultiSelectSearchMessage}
                                disabled={copyItemsLoading}
                                className={isCopySubmitted && !IsStringNullEmptyOrUndefined(copyItemsError) ? 'p-invalid' : ''}
                                onChange={(e) => setCopyCSProductCodes(e.value || [])}
                            />
                            <div className="bom-form-hint">{copyCSProductCodes.length} of {copyItems.length} line items selected</div>
                            {isCopySubmitted && <small className="text-danger font-weight-500">{copyItemsError}</small>}
                        </div>
                        <CascadeFields
                            value={copyCascade}
                            systems={systems}
                            orientations={copyOrientations}
                            orientationsLoading={copyOrientationsLoading}
                            onChange={(next) => { setCopyCascade(next); setCopyTargetOrientations([]); }}
                            multiOrientation={{ value: copyTargetOrientations, onChange: setCopyTargetOrientations }}
                            fieldClassName="form-group mb-0 bom-form-field"
                        />
                        {isCopySubmitted && !IsStringNullEmptyOrUndefined(copyTargetError) &&
                            <div className="bom-form-field full">
                                <small className="text-danger font-weight-500">{copyTargetError}</small>
                            </div>}
                        <div className="bom-form-hint full">
                            The selected line items are copied to every active model of each target orientation. Line items already mapped there are skipped.
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
}

export default BOMMapping;